/**
 * Parses the URL query string for Apollo / utm visitor params.
 *
 * Apollo outbound campaigns embed personalization tokens directly into the
 * URL, e.g. `?email=jane@acme.com&first_name=Jane&utm_source=apollo`.
 * Some templates compress them (`em`, `fn`, etc.). This module normalizes
 * both long and short variants into a single typed shape and also returns
 * the full raw query dict so unknown keys aren't lost.
 */

export interface ApolloVisitorParams {
  identity: {
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
    linkedinUrl?: string;
    phone?: string;
  };
  apolloContactId?: string;
  apolloCampaignId?: string;
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  rawParams: Record<string, string>;
  hasAnyIdentity: boolean;
  hasAnyAttribution: boolean;
}

function readFirst(
  params: URLSearchParams,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const raw = params.get(key);
    if (raw && raw.trim()) return raw.trim();
  }
  return undefined;
}

export function parseApolloParams(
  search: string | URLSearchParams,
): ApolloVisitorParams {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search;

  const rawParams: Record<string, string> = {};
  params.forEach((value, key) => {
    if (key && value) rawParams[key.toLowerCase()] = value;
  });

  const identity = {
    email: readFirst(params, ['email', 'em']),
    firstName: readFirst(params, ['first_name', 'firstname', 'fn']),
    lastName: readFirst(params, ['last_name', 'lastname', 'ln']),
    company: readFirst(params, ['company', 'co']),
    title: readFirst(params, ['title', 'ti']),
    linkedinUrl: readFirst(params, ['linkedin_url', 'linkedin', 'li']),
    phone: readFirst(params, ['phone', 'ph']),
  };

  const apolloContactId = readFirst(params, [
    'apollo_contact_id',
    'apollo_contact',
    'apolloContactId',
  ]);
  const apolloCampaignId = readFirst(params, [
    'apollo_campaign_id',
    'apollo_campaign',
    'apolloCampaignId',
  ]);

  const utm = {
    source: readFirst(params, ['utm_source']),
    medium: readFirst(params, ['utm_medium']),
    campaign: readFirst(params, ['utm_campaign']),
    content: readFirst(params, ['utm_content']),
    term: readFirst(params, ['utm_term']),
  };

  const hasAnyIdentity = Object.values(identity).some(Boolean);
  const hasAnyAttribution =
    !!apolloContactId || !!apolloCampaignId || Object.values(utm).some(Boolean);

  return {
    identity,
    apolloContactId,
    apolloCampaignId,
    utm,
    rawParams,
    hasAnyIdentity,
    hasAnyAttribution,
  };
}

/**
 * Read the current window's location.search (works under HashRouter where
 * the visible URL is /#/pilot?email=... but params can live before the hash
 * — Apollo templates typically place them in window.location.search).
 *
 * We also probe location.hash for `?` (in case the params are after the
 * hash, e.g. `/#/pilot?email=...`) so the page works either way.
 */
export function readCurrentApolloParams(): ApolloVisitorParams {
  if (typeof window === 'undefined') {
    return parseApolloParams('');
  }
  const sources: string[] = [];
  if (window.location.search) sources.push(window.location.search);
  const hash = window.location.hash || '';
  const hashQueryIndex = hash.indexOf('?');
  if (hashQueryIndex >= 0) {
    sources.push(hash.slice(hashQueryIndex));
  }
  const combined = new URLSearchParams();
  for (const src of sources) {
    const parsed = new URLSearchParams(
      src.startsWith('?') ? src.slice(1) : src,
    );
    parsed.forEach((value, key) => combined.append(key, value));
  }
  return parseApolloParams(combined);
}
